import {createAsyncThunk} from "@reduxjs/toolkit";
import {endOfDay, getUnixTime, startOfDay} from "date-fns";
import {
    EP_INFLUENCERS_INSIGHTS,
    EP_INFLUENCERS_PERFORMANCES,
    EP_INFLUENCERS_PERFORMANCES_ACTIVE_INFLUENCERS,
    EP_INFLUENCERS_PERFORMANCES_AVERAGE_CLICKS,
    EP_INFLUENCERS_PERFORMANCES_BEST_SOCIAL_MEDIA,
    EP_INFLUENCERS_PERIOD_SALES,
} from "../../Api/endPoints";
import {getData} from "../../Api/api";
import {
    chartArrayMapper,
    formatCurrencyCompactEUR,
    formatNumberChart,
    getUnixTimestampUTC
} from "../../utility/Utils";
import {salesChart} from "./influencer.mapper";
import { zonedTimeToUtc } from "date-fns-tz";

export const getInfluencerPerformances = createAsyncThunk(
    "influencer/getPerformances",
    async ({startDate, endDate, brand = 0}, thunkAPI) => {
        try {
            const params = {
                start: getUnixTime(zonedTimeToUtc(startOfDay(startDate),"GMT")),
                end: getUnixTime(zonedTimeToUtc(endOfDay(endDate),"GMT")),
                brand_id: brand,
                timezone: new Date().getTimezoneOffset()
            };

            const [perfRes, activeRes, avgClicksRes, bestSocialRes] = await Promise.allSettled([
                getData(EP_INFLUENCERS_PERFORMANCES, {params}),
                getData(EP_INFLUENCERS_PERFORMANCES_ACTIVE_INFLUENCERS, {params}),
                getData(EP_INFLUENCERS_PERFORMANCES_AVERAGE_CLICKS, {params}),
                getData(EP_INFLUENCERS_PERFORMANCES_BEST_SOCIAL_MEDIA, {params}),
            ]);

            const perf = perfRes.status === "fulfilled" ? perfRes.value : {};
            const activePayload = activeRes.status === "fulfilled" ? activeRes.value : {};
            const avgClicksPayload = avgClicksRes.status === "fulfilled" ? avgClicksRes.value : {};
            const bestSocialRow = bestSocialRes.status === "fulfilled" ? bestSocialRes.value : null;

            const averageCart = perf?.average_cart;
            const bestOfInfluencers = perf?.best_of_influencers;
            const activeInfluencersNbr = activePayload?.active_influencers_nbr;
            const averageClicksRaw =
                avgClicksPayload?.average_clicks ?? avgClicksPayload?.averageClicks;
            const bestSocialMedia = bestSocialRow
                ? {
                      total_clicks: Number(bestSocialRow.total_clicks ?? 0),
                      socialMedia:
                          bestSocialRow.socialMedia ??
                          bestSocialRow.name ??
                          bestSocialRow.source ??
                          "",
                  }
                : {total_clicks: 0, socialMedia: ""};

            const {influencerStatsData} = thunkAPI.getState().influencer;

            return {
                ...influencerStatsData,
                global: {
                    averageCart: averageCart ? formatCurrencyCompactEUR(averageCart) : 0,
                    averageClicks: averageClicksRaw != null ? formatNumberChart(averageClicksRaw) : 0,
                    bestSocialMedia,
                    activeInfluencersNbr: activeInfluencersNbr ?? 0
                },
                charts: {
                    sales: {unit: '', labels: [], datasets: []},
                    bestOfInfluencers: bestOfInfluencers ? chartArrayMapper(bestOfInfluencers, 'name', 'turnover')
                        : {labels: [], datasets: {data: []}}
                }
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getInfluencers = createAsyncThunk(
    "influencer/getInfluencers",
    async ({startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC"}, thunkAPI) => {
        try {
            const params = {
                start: getUnixTime(zonedTimeToUtc(startOfDay(startDate),"GMT")),
                end: getUnixTime(zonedTimeToUtc(endOfDay(endDate),"GMT")),
                brand_id: brand,
                offset: skip,
                count: limit,
                order_by,
                sort,
                timezone: new Date().getTimezoneOffset()
            };

            const {details_per_influencer, details_total_count} = await getData(EP_INFLUENCERS_INSIGHTS, {params});

            const {influencerStatsData} = thunkAPI.getState().influencer;

            return {
                ...influencerStatsData,
                influencers: details_per_influencer?.filter(value => Object.keys(value).length !== 0) || [],
                influencersTotal: Number(details_total_count) || details_per_influencer?.length || 0,
            }
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getSaleByInfluencerId = createAsyncThunk(
    "influencer/getSaleByInfluencerId",
    async ({startDate, endDate, brand = 0, influencer}, thunkAPI) => {
        try {
            const params = {
                start: getUnixTimestampUTC(startDate),
                end: getUnixTimestampUTC(endDate),
                brand_id: brand,
                uid: influencer,
                timezone: new Date().getTimezoneOffset()
            }

            const sales = await getData(EP_INFLUENCERS_PERIOD_SALES, {params});

            const {influencerStatsData} = thunkAPI.getState().influencer;

            return {
                ...influencerStatsData,
                charts: {
                    ...influencerStatsData?.charts,
                    sales: salesChart(sales, sales?.[0]?.type || '', startDate, influencerStatsData?.influencers)
                },
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);
